import { FunctionComponent, MouseEvent, useState } from 'react'
import { Icon } from '../../shared/Icon'
import { STATS_DISCLAIMER_TEXT } from '../../statsAssistant/StatsDisclaimer/StatsDisclaimer.component'
import { ModeConfig, ModeId, StatsButtonState } from './DictationWidget.types'
import { ModeSelector } from './ModeSelector.component'
import { WidgetPopover } from './WidgetPopover.component'
import styles from './DictationWidget.module.scss'

type Props = {
  activeMode: ModeConfig
  availableModes: ModeConfig[]
  hasProcessingTurn: boolean
  hasUnseenResult: boolean
  panelDirection: 'down' | 'up'
  modeOpen: boolean
  onMouseDown: (e: MouseEvent) => void
  /** Vrai si le geste qui se termine était un déplacement (et le consomme) : le clic est ignoré. */
  consumeDrag: () => boolean
  onPanelToggle: () => void
  onExpand: () => void
  onModeDropdownToggle: () => void
  onModeChange: (id: ModeId) => void
  setModeOpen: (open: boolean) => void
}

export const StatsModeBar: FunctionComponent<Props> = ({
  activeMode,
  availableModes,
  hasProcessingTurn,
  hasUnseenResult,
  panelDirection,
  modeOpen,
  onMouseDown,
  consumeDrag,
  onPanelToggle,
  onExpand,
  onModeDropdownToggle,
  onModeChange,
  setModeOpen,
}) => {
  const [infoOpen, setInfoOpen] = useState(false)

  const buttonState: StatsButtonState = hasProcessingTurn
    ? 'statsProcessing'
    : hasUnseenResult
      ? 'statsResultReady'
      : 'idle'
  const panelLabel = hasProcessingTurn
    ? 'Ouvrir le panneau statistiques (analyse en cours)'
    : hasUnseenResult
      ? 'Ouvrir le panneau statistiques (résultat disponible)'
      : 'Ouvrir le panneau statistiques'

  // Les boutons d'info et d'agrandissement ne vérifient pas eux-mêmes le drag :
  // le clic qui termine un déplacement est intercepté ici.
  const handleClickCapture = (event: MouseEvent) => {
    if (!consumeDrag()) return
    event.stopPropagation()
    event.preventDefault()
  }

  return (
    <div className={styles.barZone}>
      <div className={styles.bar} onMouseDown={onMouseDown} onClickCapture={handleClickCapture}>
        <button
          type="button"
          className={styles.recBtn}
          onClick={onPanelToggle}
          aria-label={panelLabel}
        >
          <span className={styles.ip} data-state={buttonState} data-mode="stats">
            <Icon icon={activeMode.iconName} size="pico" color="currentColor" />
          </span>
        </button>

        {/* Libellé + chevron dans un seul bouton, comme la barre Dictée */}
        <div className={styles.modeBtn}>
          {availableModes.length > 1 ? (
            <button
              type="button"
              className={styles.modeLabelBtn}
              onMouseDown={(e) => e.nativeEvent.stopPropagation()}
              onClick={onModeDropdownToggle}
              aria-label="Changer de mode"
              aria-expanded={modeOpen}
            >
              <span className={styles.ipLabel}>{activeMode.label}</span>
              <Icon icon="chevron" size="pico" rotate={modeOpen ? 180 : 0} color="currentColor" />
            </button>
          ) : (
            <button
              type="button"
              className={styles.modeLabelBtn}
              onClick={onPanelToggle}
              aria-label={panelLabel}
            >
              <span className={styles.ipLabel}>{activeMode.label}</span>
            </button>
          )}
        </div>

        <div className={styles.barSep} />

        <button
          type="button"
          className={styles.iconZone}
          onMouseDown={(e) => e.nativeEvent.stopPropagation()}
          onClick={() => setInfoOpen((open) => !open)}
          title="Informations sur les données"
          aria-label="Informations sur les données"
          aria-expanded={infoOpen}
          data-testid="button-stats_assistant-bar-disclaimer"
        >
          <Icon icon="infoCircle" size="micro" color="currentColor" />
        </button>

        <div className={styles.barSep} />

        <button
          type="button"
          className={styles.iconZone}
          onClick={onExpand}
          title="Ouvrir le panneau"
          aria-label="Ouvrir le panneau"
          data-testid="button-stats_assistant-bar-expand"
        >
          <Icon icon="caret" size="micro" color="currentColor" />
        </button>
      </div>

      {modeOpen && (
        <ModeSelector
          modes={availableModes}
          activeModeId={activeMode.id}
          direction={panelDirection}
          onSelect={onModeChange}
          onClose={() => setModeOpen(false)}
        />
      )}

      {infoOpen && (
        <div className={styles.popAnchorInfo}>
          <WidgetPopover
            onClose={() => setInfoOpen(false)}
            align="right"
            direction={panelDirection}
          >
            <div className={styles.popTitle}>Données</div>
            <div className={styles.popInfo}>
              <div className={styles.popInfoHint}>{STATS_DISCLAIMER_TEXT}</div>
            </div>
          </WidgetPopover>
        </div>
      )}
    </div>
  )
}
